import { Product, Purchase, analytics } from '@play-co/gcinstant';
import StateObserver from 'src/StateObserver';
import { Iap, ProductId } from 'src/replicant/iap';
import { StripeUI } from './StripeUI';

type ProvisionProductAsyncCallback = (
  purchase: Purchase,
  product: Product,
  opts: {
    consumedAtLaunch: boolean;
  },
) => Promise<void>;

type PurchaseDTO = { purchase: Purchase; product: Product; cancel?: boolean };
type Stripe = any; // TODO;

// Set to false so switch off IAP completely, even with Stripe available
const IAP = true;

// If using a live key, then Stripe should run in live mode, using live products
const LIVE_MODE = process.env.STRIPE_PUBLISHABLE_KEY.startsWith('pk_live');

/**
 * In-app purchase client based on Stripe, so that can be used for open web
 * The implementation is based on GCInstant IAP in a way that the iapClient
 * can replace is for in-app purchases
 */
class IAPClient {
  public paymentsAvailable = IAP && !!window.Stripe;
  public liveMode = LIVE_MODE;
  public catalog: Product[];
  private _onProvisionProductAsync?: ProvisionProductAsyncCallback;
  private _onPaymentsReady?: () => void;
  private _isPaymentsReady?: boolean;
  private stripe?: Stripe;
  private stripeUI: StripeUI;

  public async iapInit(options?: { productIDs: string[] }) {
    if (!this.paymentsAvailable || this.stripe) return;
    // Initialize Stripe if not instantiated yet
    if (!this.stripe) {
      this.stripe = window.Stripe(process.env.STRIPE_PUBLISHABLE_KEY);
    }
    this.catalog = this.createCatalog();
    this._isPaymentsReady = true;
    this.stripeUI = new StripeUI();
    if (this._onPaymentsReady) this._onPaymentsReady();
  }

  public async checkPendingPayments() {
    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret',
    );

    // There is no pending payments if client secret is not present
    if (!clientSecret) return null;

    // Find out any payment intent for the client
    const { paymentIntent } = await this.stripe.retrievePaymentIntent(
      clientSecret,
    );

    // Check if payment status is succeded and provision the product
    const productId = StateObserver.replicant.state.checkoutProductId;
    if (paymentIntent?.status === 'succeeded' && productId) {
      return { status: paymentIntent?.status, productId };
    } else {
      return { status: paymentIntent?.status, productId: '' };
    }
  }

  public async completePurchase(productId: string) {
    await StateObserver.invoke.clearPaymentIntent();
    const data = getPurchaseDTO(productId as ProductId);
    await this._onProvisionProductAsync(data.purchase, data.product, {
      consumedAtLaunch: false,
    });
  }

  public async getCatalogAsync() {
    return this.catalog;
  }

  private createCatalog() {
    const catalog: Product[] = [];
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });
    const ruleset = Iap.ruleset;
    for (let index = 0; index < ruleset.productIds.length; index++) {
      const productId = ruleset.productIds[index];
      const price = ruleset.prices[productId];
      const icon = '';

      catalog[index] = {
        title: productId,
        id: productId,
        description: productId,
        imageURI: icon,
        price: formatter.format(price / 100),
        currencyCode: 'usd',
      };
    }
    return catalog;
  }

  public async purchaseAsync(
    productId: string,
    payload?: string,
  ): Promise<PurchaseDTO> {
    let clientSecret = '';

    // Payment intents can be created in online mode only. If replicant is offline
    // the purchase can proceed without a clientSecret for local testing
    if (!process.env.REPLICANT_OFFLINE) {
      const result = await StateObserver.invoke.asyncCreateStripePaymentIntent({
        productId,
        liveMode: this.liveMode,
      });

      if (!result?.clientSecret) {
        throw new Error('Payment intent failed');
      }

      clientSecret = result.clientSecret;
    }

    await StateObserver.invoke.updatePaymentIntentWithProductId({ productId });

    const data = getPurchaseDTO(productId as ProductId);
    const rewards = Iap.api.getProductRewards(productId as ProductId);
    const amount = (rewards.paidGems ?? 0) + (rewards.freeGems ?? 0);
    const title = `${amount} Gems`;
    const price = data.product.price;
    const priceInt = Iap.ruleset.prices[productId];

    // Present Stripe payment form, that can return with an error if something goes wrong
    const error = await this.displayPaymentForm({
      clientSecret,
      title,
      price,
      priceInt,
    });

    // Throw an error if payment has failed that will be handled by the caller
    if (error) throw new Error('Payment failed: ' + error);

    // Reaching this point means the payment has succeded, call back the provision product function
    await this.completePurchase(productId);

    return data;
  }

  public onPaymentsReady(cb: () => void): void {
    if (this._isPaymentsReady) {
      cb();
      return;
    }
    this._onPaymentsReady = cb;
  }

  public onProvisionProductAsync(cb: ProvisionProductAsyncCallback): void {
    this._onProvisionProductAsync = cb;
  }

  private createElements(clientSecret: string, amount: number) {
    if (clientSecret) {
      return this.stripe.elements({
        appearance: {
          theme: 'stripe',
        },
        clientSecret,
      });
    }
    // Create elements without payment intent, for offline testing only
    return this.stripe.elements({
      mode: 'payment',
      currency: 'usd',
      amount: 1099,
    });
  }

  private async displayPaymentForm(args: {
    clientSecret: string;
    title: string;
    price: string;
    priceInt: number;
  }): Promise<string> {
    // Create Stripe elements
    const elements = this.createElements(args.clientSecret, args.priceInt);

    // Create Stripe payment element from elements
    const paymentElement = elements.create('payment', { layout: 'tabs' });

    // Mount payment elements into the form
    paymentElement.mount(this.stripeUI.payment);

    // Display the Stripe form with product title and price
    this.stripeUI.show(args.title, args.price);

    return new Promise((resolve, reject) => {
      analytics.pushEvent('StripeFormClose');
      // Set up close button so user can cancel the purchase
      this.stripeUI.onClose(() => {
        reject({ code: 'USER_INPUT', message: 'User closed payment form' });
      });

      // Set up purchase form submit action
      this.stripeUI.onSubmit(async () => {
        analytics.pushEvent('StripeFormSubmit');
        // Just accept the payment if on local tesing mode
        if (!args.clientSecret && process.env.REPLICANT_OFFLINE) {
          this.stripeUI.hide();
          resolve('');
          return;
        }

        // Attempt to confirm the payment - check up the form and show errors if there are any
        const { error } = await this.stripe.confirmPayment({
          elements: elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        });

        // If there is an error, allow user fixing it in the form and try again
        // otherwise the purchase has been completed
        if (error) {
          if (
            error.type === 'card_error' ||
            error.type === 'validation_error'
          ) {
            this.stripeUI.showMessage(error.message);
          } else {
            this.stripeUI.showMessage('An unexpected error occurred.');
          }
          analytics.pushEvent('StripeFormSubmitError', {
            type: error.type,
            message: error.message,
          });
          this.stripeUI.setLoading(false);
        } else {
          analytics.pushEvent('StripeFormSubmitSuccess');
          this.stripeUI.hide();
          resolve('');
        }
      });
    });
  }
}

/** Return an object containing purchase & product data */
function getPurchaseDTO(productId: ProductId): PurchaseDTO {
  const prices = Iap.ruleset.prices;
  const currency = 'USD';
  const dollarToLocalRate = 1;
  const amount = prices[productId];
  return {
    purchase: {
      currency,
      dollarToLocalRate,
      revenueGrossLocal: amount,
      revenueGrossUSD: amount / dollarToLocalRate,
    } as Purchase,
    product: iapClient.catalog.find((product) => product.id === productId),
  };
}

export const iapClient = new IAPClient();
